import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Gamepad2, Loader2, Trophy, Crown, CheckCircle2, XCircle, Clock, Hourglass, Star, ArrowLeft,
} from "lucide-react";

const PARTY_COLORS = ["#7c3aed", "#f59e0b", "#10b981", "#ef4444", "#0ea5e9", "#ec4899"];

function fireConfetti(big) {
  const burst = (opts) => confetti({ spread: 75, startVelocity: 45, ticks: 220, zIndex: 9999, colors: PARTY_COLORS, ...opts });
  burst({ particleCount: big ? 160 : 90, origin: { x: 0.5, y: 0.65 } });
  if (big) {
    setTimeout(() => burst({ particleCount: 90, angle: 60, origin: { x: 0, y: 0.75 } }), 250);
    setTimeout(() => burst({ particleCount: 90, angle: 120, origin: { x: 1, y: 0.75 } }), 450);
    setTimeout(() => burst({ particleCount: 70, origin: { x: 0.5, y: 0.5 } }), 700);
  }
}

function playVictorySound(big) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = big ? [523.25, 659.25, 783.99, 1046.5] : [523.25, 659.25, 783.99];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const t = now + i * 0.15;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.38);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + 0.42);
    });
    setTimeout(() => ctx.close().catch(() => {}), 2200);
  } catch (e) { /* ignore */ }
}

const OPT_META = [
  { color: "bg-rose-500 hover:bg-rose-600", label: "A" },
  { color: "bg-sky-500 hover:bg-sky-600", label: "B" },
  { color: "bg-amber-500 hover:bg-amber-600", label: "C" },
  { color: "bg-emerald-500 hover:bg-emerald-600", label: "D" },
];

export default function GamePlay() {
  const [pin, setPin] = useState("");
  const [gameId, setGameId] = useState(() => sessionStorage.getItem("kd_game_id") || null);
  const [joining, setJoining] = useState(false);

  const join = async () => {
    if (pin.trim().length < 4) { toast.error("Masukkan PIN yang benar"); return; }
    setJoining(true);
    try {
      const r = await api.post("/games/join", { pin: pin.trim() });
      sessionStorage.setItem("kd_game_id", r.data.id);
      setGameId(r.data.id);
      toast.success(`Berhasil gabung: ${r.data.title} 🎮`);
    } catch (e) { toast.error(e.response?.data?.detail || "PIN tidak ditemukan"); }
    finally { setJoining(false); }
  };

  const leave = () => { sessionStorage.removeItem("kd_game_id"); setGameId(null); setPin(""); };

  if (gameId) return <PlayView gameId={gameId} onLeave={leave} />;

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-violet-100 text-violet-600 flex items-center justify-center mx-auto mb-3"><Gamepad2 className="w-9 h-9" /></div>
        <h1 className="font-display text-2xl font-bold text-slate-800">Main Kuis Langsung</h1>
        <p className="text-slate-500 mt-1">Masukkan PIN dari gurumu untuk ikut bermain!</p>
      </div>
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
        <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="PIN" inputMode="numeric" className="text-center text-3xl font-mono tracking-[0.3em] h-16 rounded-xl" data-testid="game-pin-input" />
        <Button onClick={join} disabled={joining} className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 font-semibold text-lg" data-testid="game-join-btn">{joining ? <Loader2 className="w-5 h-5 animate-spin" /> : "Gabung 🎮"}</Button>
      </div>
    </div>
  );
}

function PlayView({ gameId, onLeave }) {
  const [s, setS] = useState(null);
  const [selected, setSelected] = useState(null);
  const [textAns, setTextAns] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef(null);
  const lastIdx = useRef(-1);
  const celebrated = useRef(false);

  const poll = () => api.get(`/games/${gameId}/play`).then((r) => {
    setS(r.data);
    if (r.data.current_index !== lastIdx.current) {
      lastIdx.current = r.data.current_index;
      setSelected(null); setTextAns("");
    }
  }).catch((e) => { if (e.response?.status === 404) { toast.error("Permainan sudah berakhir atau dihapus"); onLeave(); } });
  useEffect(() => { poll(); timer.current = setInterval(poll, 1500); return () => clearInterval(timer.current); }, [gameId]); // eslint-disable-line

  useEffect(() => {
    if (s && s.status === "ended" && !celebrated.current) {
      celebrated.current = true;
      const isWinner = s.my.rank === 1;
      if (isWinner || s.my.stars_awarded > 0) {
        fireConfetti(isWinner);
        playVictorySound(isWinner);
      }
    }
  }, [s]);

  const answer = async (ans) => {
    setSubmitting(true);
    try {
      await api.post(`/games/${gameId}/answer`, { q_index: s.current_index, answer: String(ans) });
      setSelected(String(ans));
      toast.success("Jawaban terkirim! ✅");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menjawab"); }
    finally { setSubmitting(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;

  const answered = s.my.answered || selected != null;

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onLeave} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="game-play-leave"><ArrowLeft className="w-4 h-4" /> Keluar</button>
        <div className="flex items-center gap-3 text-sm">
          {s.my.rank && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" data-testid="game-my-rank"><Trophy className="w-4 h-4" /> #{s.my.rank}</span>}
          <span className="inline-flex items-center gap-1 text-violet-700 font-bold" data-testid="game-my-score"><Star className="w-4 h-4" /> {s.my.score}</span>
        </div>
      </div>

      {s.status === "lobby" && (
        <div className="text-center py-16 space-y-3">
          <Hourglass className="w-12 h-12 mx-auto text-violet-400 animate-pulse" />
          <h2 className="font-display text-xl font-bold text-slate-800">{s.title}</h2>
          <p className="text-slate-500">Kamu sudah masuk! Tunggu guru memulai kuis ya... 🎉</p>
          <p className="text-sm text-slate-400">{s.player_count} pemain sudah bergabung</p>
        </div>
      )}

      {s.status === "question" && s.question && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Soal {s.question.no}/{s.total_questions}</span>
            <span className="inline-flex items-center gap-1 font-semibold text-slate-700"><Clock className="w-4 h-4" /> {s.time_left}s</span>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            {s.question.image && <img src={fileUrl(s.question.image)} alt="gambar soal" className="max-h-52 mx-auto mb-3 rounded-xl object-contain" data-testid="game-play-q-image" />}
            <h2 className="font-display text-lg sm:text-xl font-bold text-slate-800 text-center">{s.question.text}</h2>
          </div>
          {answered ? (
            <div className="bg-violet-50 border border-violet-200 rounded-2xl p-6 text-center text-violet-800" data-testid="game-answer-sent">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-violet-500" />
              <p className="font-semibold">Jawaban terkirim!</p>
              <p className="text-sm">Tunggu teman-temanmu & guru menampilkan jawaban...</p>
            </div>
          ) : s.question.type === "pg" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.question.options.map((o, oi) => (
                <button key={oi} onClick={() => answer(oi)} disabled={submitting} className={`rounded-2xl p-4 text-white font-semibold flex items-center gap-3 text-left transition-transform active:scale-95 ${OPT_META[oi].color}`} data-testid={`game-opt-${oi}`}>
                  <span className="w-8 h-8 rounded-lg bg-white/25 flex items-center justify-center font-black shrink-0">{OPT_META[oi].label}</span>
                  <span className="flex-1">{o}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <Input value={textAns} onChange={(e) => setTextAns(e.target.value)} className="rounded-xl h-12 text-center" placeholder="Ketik jawabanmu" data-testid="game-text-answer" />
              <Button onClick={() => answer(textAns)} disabled={submitting || !textAns.trim()} className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 font-semibold" data-testid="game-submit-text">Kirim Jawaban</Button>
            </div>
          )}
        </div>
      )}

      {s.status === "reveal" && s.reveal && (
        <div className="space-y-4">
          <div className={`rounded-2xl p-6 text-center text-white ${s.reveal.i_was_correct ? "bg-emerald-500" : "bg-rose-500"}`} data-testid="game-reveal-result">
            {s.reveal.i_was_correct ? <CheckCircle2 className="w-14 h-14 mx-auto mb-2" /> : <XCircle className="w-14 h-14 mx-auto mb-2" />}
            <p className="font-display text-2xl font-bold">{s.reveal.i_was_correct ? "Benar! 🎉" : "Belum tepat 😅"}</p>
            {s.reveal.i_was_correct && <p className="text-lg font-semibold mt-1">+{s.reveal.points_gained} poin</p>}
            {!s.my.answered && <p className="text-sm mt-1 opacity-90">Kamu tidak sempat menjawab</p>}
          </div>
          {!s.reveal.i_was_correct && s.question && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center text-emerald-800 text-sm" data-testid="game-correct-answer">
              Jawaban benar: <b>{s.question.type === "pg" ? `${["A", "B", "C", "D"][parseInt(s.reveal.correct_answer, 10)] || ""}. ${s.question.options[parseInt(s.reveal.correct_answer, 10)] || ""}` : s.reveal.correct_answer}</b>
            </div>
          )}
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
            <p className="font-display font-semibold text-slate-700 mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Peringkat Sementara</p>
            <TopList top={s.leaderboard_top} />
          </div>
        </div>
      )}

      {s.status === "ended" && (
        <div className="text-center py-8 space-y-4">
          {s.my.rank === 1 ? (
            <><Crown className="w-16 h-16 mx-auto text-amber-500" /><h1 className="font-display text-3xl font-black text-slate-800">JUARA! 🏆</h1></>
          ) : (
            <><Trophy className="w-14 h-14 mx-auto text-violet-400" /><h1 className="font-display text-2xl font-bold text-slate-800">Kuis Selesai!</h1></>
          )}
          <div className="bg-violet-600 text-white rounded-3xl py-6 px-8 inline-block shadow-lg" data-testid="game-final-rank">
            <p className="text-violet-200 text-sm">Peringkatmu</p>
            <p className="font-display text-5xl font-black">#{s.my.rank}</p>
            <p className="text-violet-100 font-semibold mt-1">{s.my.score} poin</p>
          </div>
          {s.my.stars_awarded > 0 && (
            <p className="inline-flex items-center gap-1.5 text-amber-600 font-bold bg-amber-50 border border-amber-200 rounded-full px-4 py-2" data-testid="game-stars-earned">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> +{s.my.stars_awarded} bintang untuk profilmu!
            </p>
          )}
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-left max-w-sm mx-auto"><TopList top={s.leaderboard_top} /></div>
          <Button onClick={onLeave} className="h-11 px-6 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="game-exit-btn">Kembali</Button>
        </div>
      )}
    </div>
  );
}

function TopList({ top }) {
  return (
    <div className="space-y-1.5" data-testid="game-top-list">
      {top.map((p, i) => (
        <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-slate-300 text-white" : i === 2 ? "bg-orange-400 text-white" : "bg-slate-200 text-slate-600"}`}>{i + 1}</span>
          <span className="flex-1 font-medium text-slate-700 truncate">{p.name}</span>
          <span className="font-display font-bold text-violet-700">{p.score}</span>
        </div>
      ))}
    </div>
  );
}
